import { type AztecAddress, Fr, TxStatus } from "@aztec/aztec.js";
import { getL2ClientAsync } from "../aztec-utils";
import { getL2TokenFromL2Address } from "../lib/singletons";

export async function doPublicTransfer(
  tokenAddr: AztecAddress,
  recipient: AztecAddress,
  amount: bigint,
  onTxSubmitted?: (txHash: string) => void,
) {
  const l2Client = await getL2ClientAsync();
  const token = await getL2TokenFromL2Address(tokenAddr.toString());
  const symbol = await token.getSymbol();
  console.log(`Transferring ${amount} ${symbol} to ${recipient}...`);

  const balance = await token.balanceOfPublic(l2Client.getAddress()).simulate( { from: l2Client.getAddress() } );
  console.log(`Current balance: ${balance}`);
  if (balance < amount) {
    throw new Error("Insufficient balance");
  }

  const tx = await token.transferPublic(recipient, amount).send({ from: l2Client.getAddress() });
  const txHash = await tx.getTxHash();
  console.log(`Transaction submitted: ${txHash}`);

  // Call the callback immediately after getting the txHash
  onTxSubmitted?.(txHash.toString());

  console.log("Waiting for receipt...");
  const receipt = await tx.wait();
  console.log("Transfer status:", receipt.status);
  if (receipt.status !== TxStatus.SUCCESS) {
    throw new Error("Transfer failed");
  }
  console.log("Transfer successful!");

  return txHash.toString();
}

export async function doPrivateTransfer(
  tokenAddr: AztecAddress,
  recipient: AztecAddress,
  amount: bigint,
  onTxSubmitted?: (txHash: string) => void,
) {
  const l2Client = await getL2ClientAsync();
  const token = await getL2TokenFromL2Address(tokenAddr.toString());

  const symbol = await token.getSymbol();
  console.log(`PRIVATELY Transferring ${amount} ${symbol} to ${recipient}...`);

  const balance = await token.balanceOfPrivate(l2Client.getAddress()).simulate( { from: l2Client.getAddress() } );
  console.log(`Current private balance: ${balance}`);
  if (balance < amount) {
    throw new Error("Insufficient balance");
  }

  // TODO: Use a correctly formatted verified ID
  const verifiedID: Fr[] & { length: 5 } = [
    Fr.fromHexString("0x01"),
    Fr.fromHexString("0x02"),
    Fr.fromHexString("0x03"),
    Fr.fromHexString("0x04"),
    Fr.fromHexString("0x05"),
  ];
  console.log("Using Verified ID:", verifiedID);

  const tx = await token.transferPrivate(recipient, amount, verifiedID).send({ from: l2Client.getAddress() });
  const txHash = await tx.getTxHash();
  console.log(`Transaction submitted: ${txHash}`);

  // Call the callback immediately after getting the txHash
  onTxSubmitted?.(txHash.toString());

  console.log("Waiting for receipt...");
  const receipt = await tx.wait();
  console.log("Transfer status:", receipt.status);
  if (receipt.status !== TxStatus.SUCCESS) {
    throw new Error("Transfer failed");
  }
  console.log("Transfer successful!");

  return txHash.toString();
}
